import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch } from "@/store/store";
import { selectSmartLink, exitSmartLink } from "@/store/slices/smartLink/smartLinkSlice";
import { applyLinkSegmentsLocal } from "@/store/slices/segments/segments.thunk";

/**
 * Ends a smart-link view-only session the moment the recipient navigates away
 * from the screen they entered it on, so the local `isApplied` override never
 * leaks into a screen that pre-checks segment filters from `isApplied` (By
 * Brand/By Market).
 *
 * Keyed on `location.pathname` vs. the `enteredPath` captured at
 * `enterSmartLink`, not on `screenConfig.segmentFilterModalMode`: two
 * By-Segment screens in different modules are both `SEGMENT_ONLY` but are
 * different pathnames, so keying on mode alone would miss that cross-module
 * leak. Keying on pathname also makes this immune to `location.search`-only
 * changes (scalar/market changes on the same screen) — those must not revert
 * the session mid-view, hence `location.search` is deliberately excluded from
 * the dependency array.
 */
export const useSmartLinkSession = (): void => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const { active, enteredPath, snapshotApplied } = useSelector(selectSmartLink);

  useEffect(() => {
    if (!active || enteredPath === null || location.pathname === enteredPath) return;
    dispatch(applyLinkSegmentsLocal(snapshotApplied ?? []));
    dispatch(exitSmartLink());
    // location.search is deliberately excluded: this must key on pathname only.
  }, [location.pathname, active, enteredPath, snapshotApplied, dispatch]);
};
